/**
 * 공지 첨부파일을 presigned URL 로 S3 에 올린다.
 *
 * 예전 구현의 문제는 세 가지였다.
 *
 * 1. 한 건이 실패해도 멈추지 않고 남은 파일을 끝까지 올린 뒤, 마지막에 성공 건수가
 *    모자라다는 이유로 전부 버렸다. 25개 중 3번째가 실패하면 뒤의 22개를 다 올린
 *    다음 통째로 폐기했다.
 * 2. 진행 상황을 알리지 않아 화면에는 '공지 등록 중입니다.' 만 떠 있었다. 캠페인
 *    1회분은 약 1GB 를 순차 전송하므로 사용자가 멈춘 것으로 보고 화면을 닫는다.
 * 3. 어느 파일에서 실패했는지 알려주지 않고, 재시도하면 성공했던 파일까지 처음부터
 *    다시 올렸다.
 *
 * 그래서 첫 실패에서 즉시 멈추고, 몇 번째를 올리는 중인지 알리고, 성공한 인덱스를
 * 호출자가 건네준 집합에 기록한다. 같은 집합을 다시 넘기면 재시도는 남은 파일만
 * 올린다. presigned URL 유효시간이 10,000초이므로 같은 화면에서의 재시도는 URL 을
 * 다시 발급받지 않아도 된다.
 */
export type NoticeAttachUploadResult = { ok: true } | { ok: false; failedIndex: number; failedName: string };

export type NoticeAttachUploadOptions = {
  onProgress?: (done: number, total: number) => void;
  /** 성공한 인덱스가 누적되는 집합. 재시도에 같은 집합을 넘기면 그 건을 건너뛴다. */
  uploaded?: Set<number>;
};

export const uploadNoticeAttachments = async (
  files: File[],
  s3DataList: { uploadURL: string }[],
  options: NoticeAttachUploadOptions = {},
): Promise<NoticeAttachUploadResult> => {
  const total = files.length;
  // 파일과 발급된 URL 이 어긋나면 엉뚱한 키에 올라가므로 올리기 전에 막는다.
  if (s3DataList.length < total) return { ok: false, failedIndex: s3DataList.length, failedName: files[s3DataList.length]?.name ?? "" };

  const uploaded = options.uploaded ?? new Set<number>();
  for (let i = 0; i < total; i++) {
    if (uploaded.has(i)) {
      options.onProgress?.(i + 1, total);
      continue;
    }
    let ok = false;
    try {
      const response = await fetch(s3DataList[i].uploadURL, {
        method: "PUT",
        headers: { "Content-Type": files[i].type, "Content-Disposition": "attachment" },
        body: files[i],
      });
      ok = response.ok;
    } catch (error) {
      // 네트워크가 끊긴 경우. 사용자에게는 파일명만 알리지만, 원인(CORS, 403, 타임아웃)이
      // 전혀 남지 않으면 운영 중에 추적할 수 없으므로 로그는 남긴다.
      console.error("notice attachment upload failed", files[i]?.name, error);
      ok = false;
    }
    if (!ok) return { ok: false, failedIndex: i, failedName: files[i].name };
    uploaded.add(i);
    options.onProgress?.(i + 1, total);
  }
  return { ok: true };
};

/** 고른 파일 묶음을 가리키는 키. 묶음이 바뀌면 재시도용 상태를 버려야 한다. */
export const noticeAttachKey = (files: File[]): string => files.map((file) => `${file.name}:${file.size}`).join("|");

/**
 * 발급받은 presigned URL 을 재시도에 다시 쓸 수 있는 시간.
 *
 * 발급 시 유효시간이 10,000초(약 2시간 46분)이므로 그보다 넉넉히 짧게 잡는다.
 */
export const NOTICE_ATTACH_URL_REUSE_MS = 90 * 60 * 1000;

/**
 * 재시도에 기존 발급분을 그대로 쓸 수 있는지 판정한다.
 *
 * 파일 묶음이 같아도 발급이 오래됐으면 버리고 다시 받는다. 그러지 않으면 URL 이 만료된
 * 뒤의 재시도가 계속 실패하는데, 키가 파일명과 크기로만 만들어지므로 사용자가 같은 파일을
 * 지웠다 다시 첨부해도 같은 상태를 재사용한다. 즉 화면을 새로 고치는 것 말고는 빠져나올
 * 길이 없는 상태가 된다.
 */
export const canReuseAttachUploadState = (
  state: { key: string; mintedAt: number; urls: { uploadURL: string }[] } | null | undefined,
  key: string,
  fileCount: number,
  now: number = Date.now(),
): boolean =>
  // 발급 수가 파일 수보다 적은 캐시는 신뢰하지 않는다. 발급이 200 으로 빈 배열을
  // 돌려주면 그 발급분이 최근 시각으로 캐시되고, 같은 묶음 재시도가 90분간 계속
  // 같은 빈 목록을 재사용해 매번 실패한다. 위 만료 함정과 같은 모양의 막힘이다.
  !!state && state.key === key && state.urls.length >= fileCount && now - state.mintedAt < NOTICE_ATTACH_URL_REUSE_MS;

export default uploadNoticeAttachments;
